import type {
  ServiceType,
  Almacenamiento,
  CandidateForm,
  ConfiguracionEnvio,
  EstadoDeFirma,
  EstadoRnr,
  ResultadoCotejo,
  EstadoDeEnvio,
  Plantilla,
  CatalogEntry,
  UpsertCatalogEntry,
  UpdateEstetica,
  EstadoFeedback,
  FeedbackView,
  ReglaDePais,
  UpsertPais,
  UpsertTemplate,
  Companero,
  PreferenciasDelCliente,
  EnviarCorreoCandidato,
  ResultadoEnvio,
  CargaDelEquipo,
  CargaDelEquipoQuery,
  Estadisticas,
  EstadisticasQuery,
  PlanillaMes,
  DestinatariosFactura,
  Avisos,
  ReportList,
  ReportQuery,
  ReportView,
  UpdateReport,
  UpdateReportImage,
  UpsertEmployment,
  UpsertVerification,
  SetAnswers,
  SetClientRequirements,
  SetEmployments,
  UpsertClientQuestions,
  UpsertEmpresasAFacturar,
  UpsertInformesHabilitados,
  AlcanceDelAnalista,
  CandidateView,
  ConsultaExterna,
  ResultadoConsulta,
  Role,
  IngresoConCodigo,
  PaginaDeAuditoria,
  PantallaDeCodigo,
  Client,
  ClientCreateOrder,
  ClientUpdateOrder,
  ClientOrderList,
  ClientOrder,
  CreateHoliday,
  Holiday,
  HousingForm,
  SetHousehold,
  CreateOrder,
  OrderDetail,
  OrderEvent,
  OrderList,
  OrderNote,
  OrderQuery,
  Session,
  UpdateOrder,
  UpsertClient,
  UpsertClientContact,
  VistaDelAlcance,
  ComunicadoEnviado,
  DestinatarioComunicado,
  DestinatariosComunicado,
  NuevoComunicado,
} from '@sadm/shared';

/** Error con el mensaje que devolvio la API, para poder mostrarlo tal cual. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors?: { campo: string; detalle: string }[],
  ) {
    super(message);
  }
}

/**
 * Renovacion de la sesion, compartida por todas las llamadas.
 *
 * Si dos pedidos fallan a la vez con 401, los dos esperan la misma renovacion
 * en vez de disparar una cada uno.
 */
let renovacion: Promise<boolean> | null = null;

/**
 * Baja un archivo de la API renovando la sesion si hace falta.
 *
 * Los `<a href="/api/...">` no pasan por aca: los resuelve el navegador y, si
 * el token de acceso vencio, muestran el JSON del error en una pestana en
 * blanco. Todo lo que sea descargar o abrir un archivo del sistema tiene que
 * pasar por esta funcion.
 */
export async function archivoDeLaApi(path: string): Promise<Blob> {
  const pedir = () => fetch(`/api${path}`, { credentials: 'include' });
  let res = await pedir();
  if (res.status === 401 && (await renovarSesion())) res = await pedir();
  if (!res.ok) {
    throw new ApiError(
      res.status === 401
        ? 'Tu sesion expiro. Volve a entrar para continuar.'
        : `No se pudo abrir el archivo (error ${res.status}).`,
      res.status,
    );
  }
  return res.blob();
}

function renovarSesion(): Promise<boolean> {
  renovacion ??= fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      renovacion = null;
    });
  return renovacion;
}

/** Las rutas que no tiene sentido reintentar: son las que dan la sesion. */
const SIN_REINTENTO = ['/auth/refresh', '/auth/login', '/auth/logout'];

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // OJO con el orden: `...init` va PRIMERO y `headers` después.
  //
  // Al revés, un llamador que traiga sus propias cabeceras —hoy el login y toda
  // la zona del candidato, que mandan el identificador del navegador— pisa el
  // objeto `headers` entero y se lleva puesto el Content-Type. Sin él, el
  // servidor no interpreta el cuerpo, le llega vacío y contesta «Datos
  // inválidos». Pasó el 17/09/2026 y dejó a todos afuera del sistema.
  let res = await fetch(`/api${path}`, {
    ...init,
    // Las cookies de sesion son httpOnly: siempre hay que mandarlas.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });

  // El token de acceso dura 15 minutos; el de refresco, 30 dias. Cuando vence
  // el primero se renueva y se repite el pedido, en vez de cortarle el trabajo
  // a quien dejo una pantalla abierta un rato.
  if (res.status === 401 && !SIN_REINTENTO.includes(path.split('?')[0])) {
    if (await renovarSesion()) {
      res = await fetch(`/api${path}`, {
        ...init,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
      });
    }
  }

  if (!res.ok) {
    let mensaje = `Error ${res.status}`;
    let errores: { campo: string; detalle: string }[] | undefined;
    try {
      const cuerpo = (await res.json()) as {
        message?: string | string[];
        errores?: { campo: string; detalle: string }[];
      };
      if (cuerpo.message) {
        mensaje = Array.isArray(cuerpo.message)
          ? cuerpo.message.join(', ')
          : cuerpo.message;
      }
      errores = cuerpo.errores;
    } catch {
      /* la respuesta no era JSON */
    }
    // "Falta el token de acceso." es el mensaje del servidor y no le dice nada
    // a quien lo lee: si la renovacion tampoco alcanzo, la sesion se perdio.
    if (res.status === 401 && !SIN_REINTENTO.includes(path.split('?')[0])) {
      mensaje = 'Tu sesión expiró. Vuelve a entrar para continuar.';
    }

    throw new ApiError(mensaje, res.status, errores);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface VerificacionFila {
  id: string;
  kind: string;
  etiqueta: string;
  source: string | null;
  status: string;
  result: string | null;
  notes: string | null;
  isAutomated: boolean;
  checkedAt: string | null;
  checkedByName: string | null;
}

export interface EmpleoFila {
  id: string;
  company: string;
  position: string | null;
  from: string | null;
  to: string | null;
  /** El período tal como lo averiguó el equipo. Si viene, manda sobre las fechas. */
  fromText: string | null;
  toText: string | null;
  /** En verdadero, lo cargo el candidato en su formulario; en falso, el equipo. */
  declaredByCandidate: boolean;
  referenceStatus: string;
  referenceContact: string | null;
  referenceComments: string | null;
  terminationReason: string | null;
}

/** Un usuario en el panel de Configuración: del equipo o de un cliente. */
export interface UsuarioDelPanel {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  clientId: string | null;
  clientName: string | null;
  isActive: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  /** A dónde le llega el enlace para restablecer, si no es a su propio correo. */
  recoveryEmail?: string | null;
  /** Si administra las cuentas del equipo. */
  gestionaUsuarios?: boolean;
  /** Si ve el Webmail de info@. */
  usaWebmail?: boolean;
}

// ---------------------------------------------------------------- Webmail

export type CarpetaWebmail = 'recibidos' | 'enviados' | 'borradores' | 'spam' | 'papelera';

export interface HiloResumen {
  id: string;
  asunto: string;
  participantes: string[];
  resumen: string;
  fecha: string;
  sinLeer: boolean;
  cantidad: number;
  adjuntos: boolean;
  /** Solo en Borradores. */
  borradorId: string | null;
}

export interface AdjuntoWebmail {
  parte: string;
  nombre: string;
  tipo: string;
  tamano: number;
}

export interface MensajeWebmail {
  id: string;
  de: string;
  para: string;
  cc: string;
  fecha: string;
  enviado: boolean;
  enPapelera: boolean;
  html: string | null;
  texto: string;
  adjuntos: AdjuntoWebmail[];
}

export interface HiloWebmail {
  id: string;
  asunto: string;
  mensajes: MensajeWebmail[];
}

export interface BorradorWebmail {
  borradorId: string;
  mensajeId: string;
  para: string[];
  cc: string[];
  cco: string[];
  asunto: string;
  texto: string;
  respondeA: string | null;
  adjuntos: AdjuntoWebmail[];
}

export interface CorreoWebmail {
  para: string[];
  cc: string[];
  cco: string[];
  asunto: string;
  texto: string;
  respondeA?: string;
  adjuntosDe?: { mensajeId: string; partes: string[] };
  borradorId?: string;
}

/** El correo va como JSON en «datos» y los archivos al lado, en el mismo multipart. */
async function subirCorreo<T>(ruta: string, correo: CorreoWebmail, archivos: File[]): Promise<T> {
  const armar = () => {
    const fd = new FormData();
    fd.append('datos', JSON.stringify(correo));
    for (const a of archivos) fd.append('adjuntos', a);
    return fd;
  };
  // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
  const pedir = () => fetch(`/api${ruta}`, { method: 'POST', credentials: 'include', body: armar() });
  let res = await pedir();
  if (res.status === 401 && (await renovarSesion())) res = await pedir();
  if (!res.ok) {
    const cuerpo = (await res.json().catch(() => ({}))) as { message?: string | string[] };
    const m = Array.isArray(cuerpo.message) ? cuerpo.message.join(', ') : cuerpo.message;
    throw new ApiError(m ?? `Error ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

export interface ManualDisponible {
  slug: string;
  titulo: string;
  publico: string;
  descripcion: string;
  sizeBytes: number | null;
  updatedAt: string | null;
  disponible: boolean;
  /** Versión vigente. Arranca en 1, que es la que viaja con el despliegue. */
  version: number;
  esVersionBase: boolean;
  notas: string | null;
  publicadoPor: string | null;
  avisado: string | null;
  avisadosA: number;
  anotacionesAbiertas: number;
  avisaA?: 'EQUIPO' | 'CLIENTES';
}

export interface VersionManual {
  id: string;
  version: number;
  notas: string | null;
  fileName: string;
  sizeBytes: number;
  publicadoPor: string | null;
  avisado: string | null;
  avisadosA: number;
  createdAt: string;
}

export interface AnotacionManual {
  id: string;
  section: string | null;
  quote: string;
  body: string;
  status: 'ABIERTA' | 'RESUELTA' | 'DESCARTADA';
  autor: string | null;
  version: number | null;
  resolvedAt: string | null;
  createdAt: string;
}

/** Una fila de la planilla, ya leída y validada. */
export interface FilaCarga {
  fila: number;
  apellido: string;
  nombre: string;
  tipoDocumento: string;
  documento: string;
  nacionalidad: string;
  email: string;
  telefono: string;
  puesto: string;
  linkedin: string | null;
  observaciones: string | null;
  ordenDeCompra: string | null;
  empresaAFacturar: string | null;
  cv: string | null;
  /** Si el CV se encontró leyendo el archivo: qué se encontró adentro. */
  cvLeido: string | null;
  /** El pedido que ya existe para este candidato: solo se le suma el CV. */
  pedidoExistente: { id: string; code: string } | null;
  errores: string[];
}

export interface VistaPreviaCarga {
  clientName: string;
  filas: FilaCarga[];
  listas: number;
  conError: number;
  paraSumarCv: number;
  cvHuerfanos: string[];
}

/** Un CV del ZIP de la carga masiva que quedó en un pedido. */
export interface CvDelZip {
  nombre: string;
  tamano: number;
  /** Otro pedido de la misma empresa al que ya se le asignó este archivo. */
  asignadoA: { id: string; code: string } | null;
}

export interface ResultadoCarga {
  creados: number;
  fallidos: number;
  omitidos: number;
  cvSumados: number;
  detalleCvSumados: { code: string; candidato: string }[];
  detalleCreados: { code: string; candidato: string }[];
  detalleFallidos: { fila: number; candidato: string; motivo: string }[];
}

export interface DocumentoBackoffice {
  id: string;
  kind: string;
  label: string;
  isRequired: boolean;
  status: string;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedBy: string | null;
  uploadedAt: string | null;
  rejectionReason: string | null;
  remindersSent: number;
  /** CV de carga masiva asignado leyendo el archivo, a confirmar: qué se encontró. */
  asignacionAConfirmar?: string | null;
  /** Un punto puede llevar varios archivos: el analitico, el titulo, sus caras. */
  archivos?: {
    id: string;
    fileName: string;
    mimeType: string | null;
    sizeBytes: number | null;
    status: string;
    rejectionReason: string | null;
    uploadedAt: string;
    /** Como quedo la firma digital del PDF. Solo en la vista del equipo. */
    firmaEstado?: EstadoDeFirma | null;
    firmaFirmante?: string | null;
    firmaFirmadoEl?: string | null;
    /** Que contesto el Registro Nacional de Reincidencia, para los penales. */
    rnrEstado?: EstadoRnr | null;
    rnrConsultadoEl?: string | null;
    /** Cómo salió comparar contra el certificado original del Registro. */
    rnrCotejo?: ResultadoCotejo | null;
    rnrTitularOriginal?: string | null;
  }[];
  /** Lo que se quitó de la lista. Vacío casi siempre: el archivo sigue guardado. */
  quitados?: {
    id: string;
    fileName: string;
    quitadoEl: string | null;
  }[];
}

export interface DailySummary {
  date: string;
  /** Lo que se conto: la jornada, o el dia si se pidio o si hoy no es habil. */
  modo: 'jornada' | 'dia';
  desde: string;
  hasta: string;
  recibidos: number;
  entregados: number;
  vencidos: number;
  porCliente: { clientId: string; clientName: string; cantidad: number }[];
}

/** Previsualizacion del correo inicial al candidato. */
export interface CorreoCandidato {
  /** El candidato: el destinatario que va a tener en produccion. */
  para: string | null;
  asunto: string;
  cuerpoHtml: string;
  /** A donde llega hoy. Con el envio de prueba, la casilla del equipo. */
  destino: string | null;
  aviso: string | null;
  impedimento: string | null;
  /** La checklist, para poder quitarle ítems a este candidato. */
  documentos: {
    id: string;
    label: string;
    isRequired: boolean;
    incluido: boolean;
  }[];
}

/**
 * La vista previa del formulario que abre Gestión.
 *
 * Es el formulario de verdad, con un token que no existe: cuando el token es
 * este, nada viaja al servidor. Cada paso "se guarda" devolviendo el formulario
 * de ejemplo, así se puede recorrer entero sin crear pedido ni candidato.
 */
export const TOKEN_VISTA_PREVIA = 'vista-previa';
let formularioDeEjemplo: CandidateView | null = null;

export function usarFormularioDeEjemplo(v: CandidateView) {
  formularioDeEjemplo = v;
}

function simulado<T>(token: string, real: () => Promise<T>, ejemplo?: () => T): Promise<T> {
  if (token !== TOKEN_VISTA_PREVIA || !formularioDeEjemplo) return real();
  const valor = ejemplo ? ejemplo() : (formularioDeEjemplo as T);
  // Una pausa corta, para que se vea el «Guardando…» como en el de verdad.
  return new Promise((r) => setTimeout(() => r(valor), 300));
}

/**
 * El identificador de este navegador.
 *
 * Se inventa una vez y queda guardado. No dice nada de la persona: solo sirve
 * para que el servidor sepa si este navegador ya confirmó el código de seis
 * dígitos que le llegó al candidato por correo. En una ventana de incógnito el
 * almacenamiento arranca vacío, así que se pide otro código, que es justamente
 * lo que Victoria pidió.
 */
function dispositivo(): string {
  const CLAVE = 'sadm.dispositivo';
  try {
    const guardado = localStorage.getItem(CLAVE);
    if (guardado) return guardado;
    const nuevo = crypto.randomUUID();
    localStorage.setItem(CLAVE, nuevo);
    return nuevo;
  } catch {
    // Sin almacenamiento —modo restringido de algunos navegadores— se pide el
    // código en cada visita. Molesto, pero se puede entrar igual.
    return crypto.randomUUID();
  }
}

/** Una llamada que lleva el identificador del navegador: la zona del candidato y el login. */
function pedidoDelCandidato<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(path, {
    ...init,
    headers: { 'x-dispositivo': dispositivo(), ...(init?.headers ?? {}) },
  });
}

/**
 * Lo que una sincronizacion sumaria y quitaria.
 *
 * Quitar una pregunta se lleva tambien la respuesta del candidato, asi que se
 * muestra antes de hacerlo y se dice cual tenia respuesta.
 */
export interface Sincronizacion {
  documentos: string[];
  preguntasQueSuma: string[];
  preguntasQueQuita: { texto: string; respondida: boolean }[];
}

export const api = {
  me: () => request<Session>('/auth/me'),
  /**
   * Login con correo y contraseña. Lo usan gestión y el portal de clientes.
   *
   * Puede devolver dos cosas: la sesión abierta, o que hace falta el código que
   * se acaba de mandar al correo porque este equipo no es conocido. Va con el
   * identificador del navegador para poder distinguirlo.
   */
  login: (email: string, password: string) =>
    pedidoDelCandidato<
      { user: unknown; mustChangePassword: boolean } | IngresoConCodigo
    >('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  /**
   * Pide el enlace para cambiar la contraseña.
   *
   * Contesta lo mismo exista o no la cuenta, asi que la pantalla muestra
   * siempre el mismo mensaje y no revela que correos estan dados de alta.
   */
  olvide: (email: string) =>
    pedidoDelCandidato<{ mensaje: string }>('/auth/olvide', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  /** Si el enlace del correo todavia sirve. */
  enlaceSirve: (token: string) =>
    pedidoDelCandidato<{ sirve: boolean }>(
      `/auth/olvide/${encodeURIComponent(token)}`,
    ),
  /** Cambia la contraseña con el enlace que llego al correo. */
  restablecer: (token: string, password: string) =>
    pedidoDelCandidato<{ mensaje: string }>('/auth/restablecer', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    }),
  /** El segundo paso, con el código de seis números que llegó al correo. */
  loginConCodigo: (email: string, password: string, codigo: string) =>
    pedidoDelCandidato<{ user: unknown; mustChangePassword: boolean }>(
      '/auth/login/codigo',
      { method: 'POST', body: JSON.stringify({ email, password, codigo }) },
    ),
  cambiarPassword: (actual: string, nueva: string) =>
    request<void>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ actual, nueva }),
    }),
  authConfig: () =>
    request<{ google: boolean; devLogin: boolean; allowedDomains: string[] }>(
      '/auth/config',
    ),
  /** Solo existe si la API tiene DEV_LOGIN_ENABLED. Ver Login.tsx. */
  devLogin: (email: string) =>
    request<unknown>('/auth/dev-login', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  orders: {
    /** Archivar: deja de verse, no se borra. Solo ADMIN. */
    archivar: (id: string, motivo: string) =>
      request<{ archivado: boolean; code: string }>(`/orders/${id}/archivar`, {
        method: 'POST',
        body: JSON.stringify({ motivo }),
      }),
    desarchivar: (id: string) =>
      request<{ archivado: boolean; code: string }>(`/orders/${id}/desarchivar`, {
        method: 'POST',
      }),
    /** Lo que el candidato va cargando, sin esperar a que termine. */
    /** El formulario que vería un candidato de este cliente. No crea nada. */
    vistaPrevia: (clientId: string, tipo?: ServiceType) =>
      request<CandidateView>(
        `/orders/vista-previa/${clientId}${tipo ? `?tipo=${tipo}` : ''}`,
      ),
    /** Lo que se le sumaría al pedido al pasarlo a otro tipo de informe. */
    previaServicio: (id: string, tipo: ServiceType) =>
      request<{ documentos: string[]; preguntas: string[] }>(`/orders/${id}/servicio/${tipo}`),
    cambiarServicio: (id: string, serviceType: ServiceType, avisarCandidato: boolean) =>
      request<{ agregados: string[]; preguntasSumadas: string[]; avisado: boolean }>(
        `/orders/${id}/servicio`,
        { method: 'POST', body: JSON.stringify({ serviceType, avisarCandidato }) },
      ),
    formularioCandidato: (id: string) =>
      request<CandidateView>(`/orders/${id}/candidate-form`),
    list: (q: Partial<OrderQuery>) => request<OrderList>(`/orders${qs(q)}`),
    get: (id: string) => request<OrderDetail>(`/orders/${id}`),
    events: (id: string) => request<OrderEvent[]>(`/orders/${id}/events`),
    /** Le escribe al analista del cliente sobre este pedido. */
    mensajeAlCliente: (id: string, mensaje: string) =>
      request<{ enviado: boolean; destino: string | null }>(
        `/orders/${id}/mensaje-cliente`,
        { method: 'POST', body: JSON.stringify({ mensaje }) },
      ),
    /** A quien ya se le aviso del informe y a quien mas se le puede reenviar. */
    destinatariosDelReenvio: (id: string) =>
      request<{
        entregado: boolean;
        yaRecibieron: string[];
        contactos: { name: string; email: string }[];
      }>(`/orders/${id}/reenvio`),
    /** Vuelve a mandar el aviso de informe listo, a los correos que se le pasen. */
    reenviarInforme: (id: string, destinos: string[]) =>
      request<{ enviado: boolean; destinos: string[] }>(`/orders/${id}/reenviar-informe`, {
        method: 'POST',
        body: JSON.stringify({ destinos }),
      }),
    /** Que cambiaria la sincronizacion de este pedido, sin hacerla. */
    previsualizarSincronizacion: (id: string) =>
      request<Sincronizacion>(`/orders/${id}/sincronizacion`),
    /** Que cambiaria en todos los pedidos en curso del cliente, sin hacerlo. */
    previsualizarSincronizacionDelCliente: (clientId: string) =>
      request<{ revisados: number; cambios: (Sincronizacion & { code: string })[] }>(
        `/orders/cliente/${clientId}/sincronizacion`,
      ),
    /** Pone al dia todos los pedidos en curso del cliente. */
    sincronizarPedidosDelCliente: (clientId: string) =>
      request<{ revisados: number; cambios: { code: string }[] }>(
        `/orders/cliente/${clientId}/sincronizar`,
        { method: 'POST' },
      ),
    /**
     * Los contactos de la empresa de este pedido.
     *
     * Sale del pedido y no de la ficha del cliente: la ficha es solo del
     * equipo interno y al analista le dejaba el desplegable vacio.
     */
    /**
     * Los correos del pedido y lo que contestaron.
     *
     * Se lee de la casilla en el momento: de las respuestas no hay copia
     * guardada, y no queremos una.
     */
    conversacion: (id: string) =>
      request<{
        mensajes: {
          id: string;
          de: string;
          para: string;
          asunto: string;
          fecha: string;
          nuestro: boolean;
          texto: string;
        }[];
        problema: string | null;
      }>(`/orders/${id}/conversacion`),
    contactosDelCliente: (id: string) =>
      request<{ contactos: { id: string; name: string; email: string }[] }>(
        `/orders/${id}/contactos-del-cliente`,
      ),
    /** Define a que analista del cliente se le entrega el informe, y copias. */
    definirDestinatarios: (
      id: string,
      requestedById: string | null,
      copiaInforme: string[],
    ) =>
      request<{ requestedByName: string | null; copiaInforme: string[] }>(
        `/orders/${id}/destinatarios`,
        { method: 'PATCH', body: JSON.stringify({ requestedById, copiaInforme }) },
      ),
    /** Pone el pedido al dia con la ficha del cliente. */
    sincronizarDocumentacion: (id: string) =>
      request<{
        agregados: string[];
        preguntasSumadas: string[];
        preguntasQuitadas: string[];
      }>(`/orders/${id}/documentacion/sincronizar`, {
        method: 'POST',
      }),
    notas: (id: string) => request<OrderNote[]>(`/orders/${id}/notes`),
    documentacionNuevaRevisada: (id: string) =>
      request<OrderDetail>(`/orders/${id}/documentacion-nueva/revisada`, { method: 'POST' }),
    agregarNota: (id: string, body: string) =>
      request<OrderNote[]>(`/orders/${id}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      }),
    create: (body: CreateOrder) =>
      request<{ id: string; code: string }>('/orders', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    linkCandidato: (id: string) =>
      request<{ url: string; expiresAt: string }>(`/orders/${id}/candidate-link`, {
        method: 'POST',
      }),
    /** El correo al candidato tal como va a salir. No emite el enlace. */
    correoCandidato: (id: string) =>
      request<CorreoCandidato>(`/orders/${id}/candidate-email`),
    /**
     * Emite el enlace y manda el correo.
     *
     * `ajustes` lleva lo que se editó en pantalla. Vacío significa "mandalo tal
     * como lo armó la plantilla", que es el caso normal.
     */
    enviarCorreoCandidato: (id: string, ajustes: EnviarCorreoCandidato = {}) =>
      request<{
        enviado: boolean;
        destino: string | null;
        detalle: string;
        url: string;
        expiresAt: string;
        /** Si salió en modo prueba: a la casilla de prueba y no al candidato. */
        esPrueba?: boolean;
      }>(`/orders/${id}/candidate-email`, {
        method: 'POST',
        body: JSON.stringify(ajustes),
      }),
    /** Contacta a varios candidatos: son N correos individuales, no uno grupal. */
    enviarCorreos: (orderIds: string[]) =>
      request<{ enviados: number; fallidos: number; resultados: ResultadoEnvio[] }>(
        '/orders/candidate-emails',
        { method: 'POST', body: JSON.stringify({ orderIds }) },
      ),
    /** Corrige el puesto y los datos del candidato. Queda en el historial. */
    corregirDatos: (id: string, body: ClientUpdateOrder) =>
      request<OrderDetail>(`/orders/${id}/datos`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    update: (id: string, body: UpdateOrder) =>
      request<OrderDetail>(`/orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    /** Reclama la documentación que falta, sin esperar al recordatorio automático. */
    recordar: (id: string) =>
      request<{ enviado: boolean; detalle: string }>(`/orders/${id}/reminder`, {
        method: 'POST',
      }),
    /** Da de baja el pedido. El motivo queda en el historial. */
    cancelar: (id: string, reason: string) =>
      request<OrderDetail>(`/orders/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    dailySummary: (modo: 'jornada' | 'dia' = 'jornada', date?: string) =>
      request<DailySummary>(`/orders/daily-summary${qs({ date, modo })}`),
    /** Cuánto tardamos: solicitud contra entrega, por cliente y por mes. */
    estadisticas: (q: EstadisticasQuery) =>
      request<Estadisticas>(`/orders/stats${qs(q)}`),
    /** Qué tiene encima cada persona del equipo y cuánto movió en el período. */
    cargaDelEquipo: (q: CargaDelEquipoQuery) =>
      request<CargaDelEquipo>(`/orders/carga-del-equipo${qs(q)}`),
    /**
     * URLs de descarga del listado. Son enlaces y no fetch: asi el navegador se
     * encarga de la descarga y del nombre del archivo.
     *
     * El CSV es el formato liviano, que se abre en cualquier lado; el xlsx es
     * la planilla para trabajar, con la cabecera fija y los filtros puestos.
     */
    exportUrl: (q: Partial<OrderQuery>) => `/api/orders/export${qs(q)}`,
    exportExcelUrl: (q: Partial<OrderQuery>) => `/api/orders/export.xlsx${qs(q)}`,
    /** El Excel del mes: una solapa por cliente, con la factura de cada pedido. */
    planilla: (mes: string, hasta?: string) =>
      request<PlanillaMes>(`/orders/planilla${qs({ mes, hasta })}`),
    planillaExcelUrl: (mes: string, hasta?: string) =>
      `/api/orders/planilla.xlsx${qs({ mes, hasta })}`,
    /** Anota la factura en varios pedidos. Null la borra. */
    facturar: (orderIds: string[], invoiceNumber: string | null) =>
      request<{ actualizados: number }>('/orders/factura', {
        method: 'PATCH',
        body: JSON.stringify({ orderIds, invoiceNumber }),
      }),
    destinatariosDeFactura: (orderIds: string[]) =>
      request<DestinatariosFactura>(`/orders/factura/destinatarios?ids=${orderIds.join(',')}`),
    /** Ruta para bajar la hoja de detalle con useArchivo (sin el prefijo /api). */
    hojaDeDetallePath: (orderIds: string[], observaciones?: string) =>
      `/orders/factura/detalle${qs({ ids: orderIds.join(','), obs: observaciones?.trim() })}`,
    enviarFactura: async (
      datos: { orderIds: string[]; para: string[]; copia: string[]; mensaje: string | null; incluirDetalle: boolean; observaciones?: string | null },
      archivo: File,
      otros: File[] = [],
    ) => {
      const fd = new FormData();
      fd.append('datos', JSON.stringify(datos));
      fd.append('archivo', archivo);
      for (const o of otros) fd.append('otros', o);
      // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
      const pedir = () => fetch('/api/orders/factura/enviar', { method: 'POST', credentials: 'include', body: fd });
      let res = await pedir();
      if (res.status === 401 && (await renovarSesion())) res = await pedir();
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => ({}))) as { message?: string | string[] };
        const m = Array.isArray(cuerpo.message) ? cuerpo.message.join(' ') : cuerpo.message;
        throw new ApiError(m ?? `Error ${res.status}`, res.status);
      }
      return (await res.json()) as { enviado: boolean; destinos: string[]; aviso: string | null };
    },
  },

  /** Zona Cliente: pedidos.socioambientalesdm.com */
  portal: {
    login: (email: string, password: string) => api.login(email, password),
    orders: (page = 1, buscar?: string) =>
      request<ClientOrderList>(`/portal/orders${qs({ page, buscar })}`),
    /** Qué campos le pide esta empresa al cargar un pedido. */
    preferencias: () =>
      request<PreferenciasDelCliente>('/portal/preferencias'),
    /** Compañeros del portal a los que se les puede dar seguimiento del pedido. */
    companeros: () => request<Companero[]>('/portal/companeros'),

    /**
     * Carga masiva desde el portal.
     *
     * No lleva clientId: la empresa sale de la sesión, no de la petición.
     */
    urlPlantillaMasiva: () => '/api/portal/bulk/template',
    vistaPreviaMasiva: (planilla: File, zip: File | null) =>
      subirCarga<VistaPreviaCarga>('/api/portal/bulk/preview', planilla, zip),
    crearMasiva: (planilla: File, zip: File | null) =>
      subirCarga<ResultadoCarga>('/api/portal/bulk/create', planilla, zip),
    order: (id: string) => request<ClientOrder>(`/portal/orders/${id}`),
    create: (body: ClientCreateOrder) =>
      request<ClientOrder>('/portal/orders', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    /** Corrige los datos que se cargaron mal: un DNI, un apellido. */
    editar: (id: string, body: ClientUpdateOrder) =>
      request<ClientOrder>(`/portal/orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    /** Pasar el pedido a otro de los informes habilitados. */
    cambiarServicio: (id: string, serviceType: ServiceType) =>
      request<{ agregados: string[]; preguntasSumadas: string[]; avisado: boolean }>(
        `/portal/orders/${id}/servicio`,
        { method: 'POST', body: JSON.stringify({ serviceType }) },
      ),
    /** Cancelacion dentro de la ventana permitida. La API valida el plazo. */
    cancelar: (id: string, reason?: string) =>
      request<ClientOrder>(`/portal/orders/${id}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason ?? null }),
      }),
    /** Adjunta el CV u otro archivo a un pedido ya creado. */
    adjuntar: async (orderId: string, archivo: File, kind: 'CV' | 'OTRO') => {
      const fd = new FormData();
      fd.append('archivo', archivo);
      // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
      const res = await fetch(
        `/api/portal/orders/${orderId}/documents?kind=${kind}`,
        { method: 'POST', credentials: 'include', body: fd },
      );
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(cuerpo.message ?? `Error ${res.status}`, res.status);
      }
      return (await res.json()) as { fileName: string; sizeBytes: number };
    },
  },

  /** Feriados: no cuentan como dia habil en el calculo de plazos. */
  holidays: {
    list: () => request<Holiday[]>('/holidays'),
    create: (body: CreateHoliday) =>
      request<Holiday>('/holidays', { method: 'POST', body: JSON.stringify(body) }),
    remove: (id: string) => request<void>(`/holidays/${id}`, { method: 'DELETE' }),
  },

  /**
   * Zona Candidato: solicitud.socioambientalesdm.com
   * Sin sesion. La autorizacion es el token del link.
   */
  candidate: {
    ver: (token: string) =>
      simulado(token, () =>
        pedidoDelCandidato<CandidateView | PantallaDeCodigo>(`/candidate/${token}`),
      ),
    /** Manda al correo del candidato el código de este navegador. */
    pedirCodigo: (token: string) =>
      pedidoDelCandidato<{ enviadoA: string; minutos: number }>(`/candidate/${token}/codigo`, {
        method: 'POST',
      }),
    confirmarCodigo: (token: string, codigo: string) =>
      pedidoDelCandidato<CandidateView>(`/candidate/${token}/codigo/confirmar`, {
        method: 'POST',
        body: JSON.stringify({ codigo }),
      }),
    guardarDatos: (token: string, body: CandidateForm) =>
      simulado(token, () =>
        pedidoDelCandidato<CandidateView>(`/candidate/${token}/datos`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      ),
    guardarGrupoFamiliar: (token: string, body: SetHousehold) =>
      simulado(token, () =>
        pedidoDelCandidato<CandidateView>(`/candidate/${token}/grupo-familiar`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      ),
    guardarVivienda: (token: string, body: HousingForm) =>
      simulado(token, () =>
        pedidoDelCandidato<CandidateView>(`/candidate/${token}/vivienda`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      ),
    guardarEmpleos: (token: string, body: SetEmployments) =>
      simulado(token, () =>
        pedidoDelCandidato<CandidateView>(`/candidate/${token}/empleos`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      ),
    guardarRespuestas: (token: string, body: SetAnswers) =>
      simulado(token, () =>
        pedidoDelCandidato<CandidateView>(`/candidate/${token}/preguntas`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      ),
    subirDocumento: async (token: string, documentId: string, archivo: File) => {
      if (token === TOKEN_VISTA_PREVIA && formularioDeEjemplo) {
        // En la vista previa el archivo no sale de la computadora: solo se
        // marca como subido, para ver cómo queda.
        const ejemplo: CandidateView = {
          ...formularioDeEjemplo,
          documents: formularioDeEjemplo.documents.map((d) =>
            d.id === documentId ? { ...d, status: 'RECIBIDO', fileName: archivo.name } : d,
          ),
        };
        formularioDeEjemplo = ejemplo;
        return simulado(token, () => Promise.resolve(ejemplo));
      }
      const fd = new FormData();
      fd.append('archivo', archivo);
      // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
      const res = await fetch(`/api/candidate/${token}/documentos/${documentId}`, {
        method: 'POST',
        headers: { 'x-dispositivo': dispositivo() },
        body: fd,
      });
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(cuerpo.message ?? `Error ${res.status}`, res.status);
      }
      return (await res.json()) as CandidateView;
    },
    enviar: (token: string, consentimiento: boolean) =>
      simulado(
        token,
        () =>
          pedidoDelCandidato<CandidateView & { faltantes: number }>(
            `/candidate/${token}/enviar`,
            { method: 'POST', body: JSON.stringify({ consentimiento }) },
          ),
        () => ({ ...formularioDeEjemplo!, faltantes: 0 }),
      ),
  },

  /**
   * El registro de auditoria. Solo lectura y solo para administradores: se
   * escribe desde la API en cada accion sensible y no se edita desde ningun lado.
   */
  auditoria: {
    consultar: (q: {
      buscar?: string;
      accion?: string;
      desde?: string;
      hasta?: string;
      page?: number;
    }) => request<PaginaDeAuditoria>(`/settings/auditoria${qs(q)}`),
  },

  /**
   * Las fuentes externas del informe.
   *
   * El Banco Central, las bases internacionales y los registros de titulos.
   * Nosis y ANSES entran por el mismo camino cuando haya credenciales.
   */
  fuentes: {
    list: (orderId: string) =>
      request<ConsultaExterna[]>(`/orders/${orderId}/fuentes`),
    bcra: (orderId: string) =>
      request<ConsultaExterna>(`/orders/${orderId}/fuentes/bcra`, { method: 'POST' }),
    /** OFAC, RePET, FBI e Interpol, por el nombre del candidato. */
    internacionales: (orderId: string) =>
      request<ResultadoConsulta[]>(`/orders/${orderId}/fuentes/internacionales`, { method: 'POST' }),
    /** Titulos de Buenos Aires, ReFE y Santa Fe, por DNI. */
    titulos: (orderId: string) =>
      request<ResultadoConsulta[]>(`/orders/${orderId}/fuentes/titulos`, { method: 'POST' }),
    /** FDA, OIG y SAM: solo cuando el cliente las pidió. */
    otrasBases: (orderId: string) =>
      request<ResultadoConsulta[]>(`/orders/${orderId}/fuentes/otras-bases`, { method: 'POST' }),
    /** La ultima vez que se apreto cada boton, con lo que contesto. */
    ultimas: (orderId: string) =>
      request<Record<'internacionales' | 'titulos' | 'otras', UltimaConsulta | null>>(
        `/orders/${orderId}/fuentes/ultimas`,
      ),
  },

  avisos: {
    /** Lo ultimo que paso en los pedidos, para la campanita del equipo. */
    listar: () => request<Avisos>('/avisos'),
    marcarVistos: () => request<{ ok: true }>('/avisos/vistos', { method: 'POST' }),
  },

  archivosDeDocumento: {
    /** Aprobar o rechazar un archivo suelto, sin tocar el resto del punto. */
    revisar: (fileId: string, status: 'APROBADO' | 'RECHAZADO', motivo: string | null) =>
      request<DocumentoBackoffice[]>(`/document-files/${fileId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, rejectionReason: motivo }),
      }),
    quitar: (fileId: string) =>
      request<DocumentoBackoffice[]>(`/document-files/${fileId}`, { method: 'DELETE' }),
  },

  informes: {
    /** La biblioteca: todo lo que se armo, para volver a un informe viejo. */
    listar: (q: Partial<ReportQuery>) => request<ReportList>(`/reports${qs(q)}`),
    get: (orderId: string) => request<ReportView>(`/orders/${orderId}/report`),
    /**
     * Pasa al informe un archivo que subió el candidato.
     *
     * `archivoId` dice cuál: un punto de la checklist puede llevar varios —el
     * analítico, el título y sus dos caras— y antes solo entraba el primero.
     */
    sumarDocumento: (orderId: string, documentId: string, archivoId?: string) =>
      request<ReportView>(
        `/orders/${orderId}/report/desde-documento/${documentId}` +
          (archivoId ? `?archivo=${archivoId}` : ''),
        { method: 'POST' },
      ),
    update: (orderId: string, body: UpdateReport) =>
      request<ReportView>(`/orders/${orderId}/report`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    /** Con que texto sale cada consulta de la empresa, o si no sale. */
    definirPreguntas: (
      orderId: string,
      preguntas: { id: string; reportAnswer: string | null; hideAnswer: boolean }[],
    ) =>
      request<ReportView>(`/orders/${orderId}/report/preguntas`, {
        method: 'PUT',
        body: JSON.stringify({ preguntas }),
      }),
    /** Vuelve a avisarle al cliente un informe ya entregado, con la version nueva. */
    reenviar: (orderId: string) =>
      request<ReportView>(`/orders/${orderId}/report/reenviar`, { method: 'POST' }),
    urlPdf: (orderId: string) => `/api/orders/${orderId}/report/pdf`,
    /** Version editable, para seguir trabajando el informe fuera del sistema. */
    urlWord: (orderId: string) => `/api/orders/${orderId}/report/docx`,

    /** La imagen misma, para la miniatura del editor. */
    /**
     * La miniatura de una imagen del informe.
     *
     * `version` va para que el navegador no muestre la copia vieja despues de
     * girarla. Sin esto la imagen se gira de verdad pero en pantalla no se
     * mueve, y parece que el boton no hizo nada.
     *
     * Se le pasa el peso del archivo: al girar se vuelve a escribir y el peso
     * cambia. No es imposible que dos versiones pesen exactamente lo mismo,
     * pero si pasara alcanza con recargar la pantalla.
     */
    urlImagen: (imagenId: string, version?: number | string) =>
      `/api/report-images/${imagenId}/file${version ? `?v=${version}` : ''}`,
    subirImagen: async (
      orderId: string,
      archivo: File,
      section: string,
      caption: string,
    ) => {
      const fd = new FormData();
      fd.append('archivo', archivo);
      // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
      const res = await fetch(
        `/api/orders/${orderId}/report/images${qs({ section, caption })}`,
        { method: 'POST', credentials: 'include', body: fd },
      );
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(cuerpo.message ?? `Error ${res.status}`, res.status);
      }
      return (await res.json()) as ReportView;
    },
    actualizarImagen: (imagenId: string, body: UpdateReportImage) =>
      request<ReportView>(`/report-images/${imagenId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    borrarImagen: (imagenId: string) =>
      request<ReportView>(`/report-images/${imagenId}`, { method: 'DELETE' }),
    /**
     * Gira una imagen del informe.
     *
     * Va acá, donde se ve la miniatura: girar a ciegas desde la checklist de
     * documentación termina en girar dos veces.
     */
    rotarImagen: (imagenId: string, grados: 90 | 180 | 270 = 90) =>
      request<ReportView>(`/report-images/${imagenId}/rotar`, {
        method: 'POST',
        body: JSON.stringify({ grados }),
      }),

    verificaciones: (orderId: string) =>
      request<VerificacionFila[]>(`/orders/${orderId}/verifications`),
    agregarVerificacion: (orderId: string, body: UpsertVerification) =>
      request<VerificacionFila[]>(`/orders/${orderId}/verifications`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    guardarVerificacion: (orderId: string, vid: string, body: UpsertVerification) =>
      request<VerificacionFila[]>(`/orders/${orderId}/verifications/${vid}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    borrarVerificacion: (orderId: string, vid: string) =>
      request<VerificacionFila[]>(`/orders/${orderId}/verifications/${vid}`, {
        method: 'DELETE',
      }),

    empleos: (orderId: string) => request<EmpleoFila[]>(`/orders/${orderId}/employments`),
    agregarEmpleo: (orderId: string, body: UpsertEmployment) =>
      request<EmpleoFila[]>(`/orders/${orderId}/employments`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    guardarEmpleo: (orderId: string, eid: string, body: UpsertEmployment) =>
      request<EmpleoFila[]>(`/orders/${orderId}/employments/${eid}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    /**
     * Sube o baja un empleo en la lista.
     *
     * El orden lo fija el equipo: el período se escribe a mano y puede decir
     * «mediados de 2019», que no es una fecha con la que ordenar solo.
     */
    moverEmpleo: (orderId: string, eid: string, hacia: 'arriba' | 'abajo') =>
      request<EmpleoFila[]>(`/orders/${orderId}/employments/${eid}/orden`, {
        method: 'PUT',
        body: JSON.stringify({ hacia }),
      }),
    borrarEmpleo: (orderId: string, eid: string) =>
      request<EmpleoFila[]>(`/orders/${orderId}/employments/${eid}`, {
        method: 'DELETE',
      }),
  },

  /**
   * Carga masiva: una planilla con muchos candidatos y un ZIP con sus CV.
   *
   * `vistaPrevia` no crea nada: muestra qué se leyó y qué falla. Recién
   * `crear` da de alta los pedidos.
   */
  webmail: {
    contadores: () => request<{ recibidosSinLeer: number }>('/webmail/contadores'),
    hilos: (carpeta: CarpetaWebmail, q: string, pagina?: string) =>
      request<{ hilos: HiloResumen[]; siguiente: string | null }>(
        `/webmail/hilos${qs({ carpeta, q, pagina })}`,
      ),
    hilo: (id: string) => request<HiloWebmail>(`/webmail/hilos/${id}`),
    modificar: (id: string, accion: 'papelera' | 'restaurar' | 'leido' | 'noLeido') =>
      request<{ ok: true }>(`/webmail/hilos/${id}/${accion}`, { method: 'POST' }),
    /** Ruta del adjunto, para AbrirArchivo / BajarArchivo. */
    rutaAdjunto: (mensajeId: string, parte: string) =>
      `/webmail/mensajes/${mensajeId}/adjuntos/${parte}`,
    borrador: (id: string) => request<BorradorWebmail>(`/webmail/borradores/${id}`),
    eliminarBorrador: (id: string) =>
      request<{ ok: true }>(`/webmail/borradores/${id}`, { method: 'DELETE' }),
    guardarBorrador: (correo: CorreoWebmail, archivos: File[]) =>
      subirCorreo<{ borradorId: string; mensajeId: string | null }>(
        '/webmail/borradores',
        correo,
        archivos,
      ),
    enviar: (correo: CorreoWebmail, archivos: File[]) =>
      subirCorreo<{ id: string; hiloId: string }>('/webmail/enviar', correo, archivos),
  },
  bulk: {
    urlPlantilla: () => '/api/bulk/template',
    vistaPrevia: (clientId: string, planilla: File, zip: File | null) =>
      subirCarga<VistaPreviaCarga>(`/api/bulk/${clientId}/preview`, planilla, zip),
    /**
     * `requestedById` es el analista del cliente para toda la tanda.
     * `asignaciones`, los CV elegidos a mano en la vista previa: por número de
     * fila, el nombre del archivo del ZIP o null para dejarla sin CV.
     */
    crear: (
      clientId: string,
      planilla: File,
      zip: File | null,
      requestedById: string,
      asignaciones: Record<number, string | null> = {},
    ) =>
      subirCarga<ResultadoCarga>(`/api/bulk/${clientId}/create`, planilla, zip, {
        requestedById,
        asignaciones: Object.keys(asignaciones).length > 0 ? JSON.stringify(asignaciones) : '',
      }),
    /** El ZIP de CV sin asignar que quedó en un pedido de una carga masiva. */
    cvDelZip: (orderId: string) =>
      request<{ archivos: CvDelZip[] }>(`/bulk/pedido/${orderId}/zip`),
    rutaCvDelZip: (orderId: string, nombre: string) =>
      `/bulk/pedido/${orderId}/zip/archivo?nombre=${encodeURIComponent(nombre)}`,
    asignarCvDelZip: (orderId: string, nombre: string) =>
      request<{ archivos: CvDelZip[] }>(`/bulk/pedido/${orderId}/zip/asignar`, {
        method: 'POST',
        body: JSON.stringify({ nombre }),
      }),
  },

  /** Configuración: los usuarios de las dos zonas. */
  usuarios: {
    list: () => request<{ users: UsuarioDelPanel[] }>('/users'),
    estado: (id: string, isActive: boolean) =>
      request<{ users: UsuarioDelPanel[] }>(`/users/${id}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      }),
    /** Corrige un correo mal tipeado. No toca nada mas del usuario. */
    cambiarCorreo: (id: string, email: string) =>
      request<{ users: UsuarioDelPanel[] }>(`/users/${id}/correo`, {
        method: 'PATCH',
        body: JSON.stringify({ email }),
      }),
    /** Corrige el nombre. Si tambien es analista del cliente, se corrige ahi. */
    cambiarNombre: (id: string, name: string) =>
      request<{ users: UsuarioDelPanel[] }>(`/users/${id}/nombre`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      }),
    /** Cambia el rol y, si queda de administrador, si administra las cuentas. */
    cambiarRol: (id: string, role: string, gestionaUsuarios: boolean) =>
      request<{ users: UsuarioDelPanel[] }>(`/users/${id}/rol`, {
        method: 'PATCH',
        body: JSON.stringify({ role, gestionaUsuarios }),
      }),
    /** Habilita o quita el Webmail de info@. */
    cambiarWebmail: (id: string, usaWebmail: boolean) =>
      request<{ users: UsuarioDelPanel[] }>(`/users/${id}/webmail`, {
        method: 'PATCH',
        body: JSON.stringify({ usaWebmail }),
      }),
    /** Devuelve la contraseña nueva una sola vez: no se guarda ni se manda. */
    restablecer: (id: string) =>
      request<{ email: string; passwordTemporal: string }>(
        `/users/${id}/password`,
        { method: 'POST' },
      ),
    /** Alta de alguien del portal de un cliente, desde el mismo panel. */
    crearDeCliente: (clientId: string, email: string, name: string) =>
      request<{ email: string; name: string | null; passwordTemporal: string }>(
        '/users/cliente',
        { method: 'POST', body: JSON.stringify({ clientId, email, name }) },
      ),
    /** Qué clientes y qué pedidos ve un analista. */
    alcance: (id: string) => request<VistaDelAlcance>(`/users/${id}/alcance`),
    definirAlcance: (id: string, body: AlcanceDelAnalista) =>
      request<VistaDelAlcance>(`/users/${id}/alcance`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },

  /** Configuración: cómo sale cada correo, de verdad o a una casilla de prueba. */
  /** Cuanto pesa lo guardado en Drive y desde cuando. Solo ADMIN. */
  almacenamiento: {
    estado: (recalcular = false) =>
      request<Almacenamiento>(
        `/settings/almacenamiento${recalcular ? '?recalcular=true' : ''}`,
      ),
    /** Lo que entra en el plan, en GB. Null deja de informar lo disponible. */
    capacidad: (gb: number | null) =>
      request<{ capacidadBytes: number | null }>('/settings/almacenamiento/capacidad', {
        method: 'PUT',
        body: JSON.stringify({ gb }),
      }),
  },

  envios: {
    estado: () => request<EstadoDeEnvio[]>('/settings/envios'),
    guardar: (body: ConfiguracionEnvio) =>
      request<EstadoDeEnvio[]>('/settings/envios', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
  },

  /**
   * Los avisos por correo a los usuarios de los clientes. Solo ADMIN.
   */
  comunicados: {
    listar: () => request<ComunicadoEnviado[]>('/comunicados'),
    destinatarios: (filtro: DestinatariosComunicado) =>
      request<DestinatarioComunicado[]>('/comunicados/destinatarios', {
        method: 'POST',
        body: JSON.stringify(filtro),
      }),
    prueba: (c: NuevoComunicado) =>
      request<{ enviadoA: string }>('/comunicados/prueba', {
        method: 'POST',
        body: JSON.stringify(c),
      }),
    enviar: (c: NuevoComunicado) =>
      request<ComunicadoEnviado>('/comunicados', { method: 'POST', body: JSON.stringify(c) }),
  },
  /**
   * Lo que los usuarios nos van pidiendo.
   *
   * Mandar puede cualquiera que entre a Gestión: si hay que pedir permiso para
   * avisar que algo se ve raro, nadie avisa. Leer la bandeja es de ADMIN.
   */
  feedback: {
    enviar: async (fd: FormData) => {
      // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
      const res = await fetch('/api/feedback', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(cuerpo.message ?? `Error ${res.status}`, res.status);
      }
      return (await res.json()) as { id: string };
    },

    list: (todos: boolean) =>
      request<FeedbackView[]>(`/feedback${todos ? '?todos=1' : ''}`),

    urlCaptura: (id: string) => `/api/feedback/${id}/captura`,
    urlVideo: (id: string) => `/api/feedback/${id}/video`,

    estado: (id: string, estado: EstadoFeedback) =>
      request<FeedbackView[]>(`/feedback/${id}/estado`, {
        method: 'PATCH',
        body: JSON.stringify({ estado }),
      }),

    borrar: (id: string) =>
      request<FeedbackView[]>(`/feedback/${id}`, { method: 'DELETE' }),
  },

  /**
   * Configuración: qué documentación se le pide a cada nacionalidad.
   *
   * Esta tabla vivía en el código. Ahora la edita el equipo: agregar un país,
   * corregir cómo se llama el organismo o decidir que un documento no aplica.
   */
  /**
   * El catálogo de documentos e ítems. De acá salen las opciones de la lista de
   * cada cliente, y lo edita el equipo desde Configuración.
   */
  catalogo: {
    list: () => request<CatalogEntry[]>('/settings/catalogo'),
    guardar: (body: UpsertCatalogEntry) =>
      request<CatalogEntry[]>('/settings/catalogo', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    borrar: (id: string) =>
      request<CatalogEntry[]>(`/settings/catalogo/${id}`, { method: 'DELETE' }),
  },

  /** Cómo se ve el formulario del candidato de una empresa. */
  estetica: {
    actualizar: (clientId: string, body: UpdateEstetica) =>
      request<Client>(`/clients/${clientId}/estetica`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    urlImagen: (clientId: string, cual: 'logo' | 'portada', version = '') =>
      `/api/clients/${clientId}/estetica/${cual}${version ? `?v=${version}` : ''}`,
    subir: async (clientId: string, cual: 'logo' | 'portada', archivo: File) => {
      const fd = new FormData();
      fd.append('archivo', archivo);
      // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
      const res = await fetch(`/api/clients/${clientId}/estetica/${cual}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(cuerpo.message ?? `Error ${res.status}`, res.status);
      }
      return (await res.json()) as Client;
    },
    quitar: (clientId: string, cual: 'logo' | 'portada') =>
      request<Client>(`/clients/${clientId}/estetica/${cual}`, { method: 'DELETE' }),
  },

  /** Las preguntas propias que este cliente le hace a sus candidatos. */
  clientQuestions: {
    guardar: (clientId: string, body: UpsertClientQuestions) =>
      request<Client>(`/clients/${clientId}/questions`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  /** Que informes puede pedir este cliente desde su portal. */
  informesHabilitados: {
    guardar: (clientId: string, body: UpsertInformesHabilitados) =>
      request<Client>(`/clients/${clientId}/informes-habilitados`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  /** Las empresas del grupo a las que se le puede facturar a este cliente. */
  empresasAFacturar: {
    guardar: (clientId: string, body: UpsertEmpresasAFacturar) =>
      request<Client>(`/clients/${clientId}/empresas-a-facturar`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },

  paises: {
    list: () => request<ReglaDePais[]>('/settings/paises'),
    guardar: (body: UpsertPais) =>
      request<ReglaDePais[]>('/settings/paises', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    borrar: (id: string) =>
      request<ReglaDePais[]>(`/settings/paises/${id}`, { method: 'DELETE' }),
  },

  /** Configuración: las plantillas de todos los correos que manda el sistema. */
  plantillas: {
    list: () => request<Plantilla[]>('/settings/templates'),
    guardar: (body: UpsertTemplate) =>
      request<Plantilla[]>('/settings/templates', {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    borrar: (id: string) =>
      request<Plantilla[]>(`/settings/templates/${id}`, { method: 'DELETE' }),
  },

  manuales: {
    list: () => request<ManualDisponible[]>('/manuals'),
    urlArchivo: (slug: string) => `/api/manuals/${slug}/file`,

    versiones: (slug: string) => request<VersionManual[]>(`/manuals/${slug}/versions`),
    /** Publica una versión nueva sin tocar el servidor. */
    publicar: async (slug: string, archivo: File, notas: string) => {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('notas', notas);
      // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
      const res = await fetch(`/api/manuals/${slug}/versions`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(cuerpo.message ?? `Error ${res.status}`, res.status);
      }
      return (await res.json()) as ManualDisponible[];
    },
    /** Avisa por correo. Va aparte de publicar: son dos decisiones distintas. */
    avisar: (slug: string) =>
      request<{ enviados: number; detalle: string }>(`/manuals/${slug}/notify`, {
        method: 'POST',
      }),

    anotaciones: (slug: string) =>
      request<AnotacionManual[]>(`/manuals/${slug}/notes`),
    anotar: (slug: string, body: { section?: string | null; quote: string; body: string }) =>
      request<AnotacionManual[]>(`/manuals/${slug}/notes`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    cambiarAnotacion: (id: string, status: 'ABIERTA' | 'RESUELTA' | 'DESCARTADA') =>
      request<AnotacionManual[]>(`/manuals/notes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    borrarAnotacion: (id: string) =>
      request<AnotacionManual[]>(`/manuals/notes/${id}`, { method: 'DELETE' }),
  },

  documentos: {
    delPedido: (orderId: string) =>
      request<DocumentoBackoffice[]>(`/orders/${orderId}/documents`),
    revisar: (id: string, status: string, rejectionReason?: string) =>
      request<DocumentoBackoffice[]>(`/documents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, rejectionReason }),
      }),
    urlArchivo: (id: string) => `/api/documents/${id}/file`,
    confirmarAsignacion: (id: string, esDelCandidato: boolean) =>
      request<DocumentoBackoffice[]>(`/documents/${id}/asignacion`, {
        method: 'POST',
        body: JSON.stringify({ esDelCandidato }),
      }),
    /**
     * Le reclama al candidato un documento puntual.
     *
     * Distinto del recordatorio del pedido, que lista todo lo que falta: este
     * nombra una sola cosa. Un reclamo que nombra una cosa se responde; uno que
     * lista seis se archiva.
     */
    reclamar: (id: string) =>
      request<{ enviado: boolean; detalle: string }>(`/documents/${id}/reminder`, {
        method: 'POST',
      }),
    /**
     * Le pregunta algo al candidato sobre un documento, sin rechazarlo.
     *
     * El escalón entre aprobar y rechazar: rechazar le pide que reenvíe, y eso
     * no sirve cuando el documento puede estar bien y lo que falta es una
     * explicación.
     */
    consultar: (id: string, consulta: string) =>
      request<{ enviado: boolean; detalle: string }>(`/documents/${id}/consulta`, {
        method: 'POST',
        body: JSON.stringify({ consulta }),
      }),
    /**
     * Saca un archivo de un punto de documentación.
     *
     * Es distinto de rechazarlo: rechazar es un mensaje al candidato sobre algo
     * que él mandó. Esto es para lo que subió el equipo y quedó donde no iba.
     */
    quitarArchivo: (fileId: string) =>
      request<DocumentoBackoffice[]>(`/document-files/${fileId}`, { method: 'DELETE' }),
    /**
     * Gira un archivo que vino torcido.
     *
     * Se gira el archivo de verdad, no la pantalla: el documento después entra
     * al informe y se guarda en Drive, y de nada sirve verlo derecho acá y
     * acostado en los otros dos lados.
     */
    /**
     * Devuelve a la lista un archivo que se había quitado.
     *
     * «Quitar» ya no borra: marca el archivo y lo saca de la vista, pero el
     * archivo queda guardado. Un clic de más no puede costar la documentación
     * de un candidato.
     */
    recuperarArchivo: (fileId: string) =>
      request<DocumentoBackoffice[]>(`/document-files/${fileId}/recuperar`, {
        method: 'POST',
      }),
    rotarArchivo: (fileId: string, grados: 90 | 180 | 270 = 90) =>
      request<DocumentoBackoffice[]>(`/document-files/${fileId}/rotar`, {
        method: 'POST',
        body: JSON.stringify({ grados }),
      }),
    /**
     * El equipo adjunta lo que llegó por fuera del circuito. Con `documentId` va
     * a ese casillero; con `nuevo`, a uno que se agrega; sin ninguno, la API lo
     * ubica por el tipo.
     */
    subir: async (
      orderId: string,
      archivo: File,
      kind: string,
      destino?: { documentId?: string; nuevo?: boolean },
    ) => {
      const fd = new FormData();
      fd.append('archivo', archivo);
      const params = new URLSearchParams({ kind });
      if (destino?.documentId) params.set('documentId', destino.documentId);
      if (destino?.nuevo) params.set('nuevo', 'true');
      const res = await fetch(`/api/orders/${orderId}/documents?${params}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const cuerpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new ApiError(cuerpo.message ?? `Error ${res.status}`, res.status);
      }
      return (await res.json()) as DocumentoBackoffice[];
    },
  },

  /**
   * El pase para abrir el portal en nombre de una empresa.
   *
   * Vale un minuto: se pide y se usa en el mismo clic.
   */
  paseDePortal: (clientId: string) =>
    request<{ pase: string; cliente: string }>('/auth/portal-como-cliente', {
      method: 'POST',
      body: JSON.stringify({ clientId }),
    }),

  /** La otra punta, ya dentro del portal. */
  canjearPaseDePortal: (pase: string) =>
    request<{ cliente: string }>('/auth/portal-como-cliente/canjear', {
      method: 'POST',
      body: JSON.stringify({ pase }),
    }),

  /** Integrantes del equipo, para asignarles pedidos. */
  /** Suma a alguien del equipo de DM. Devuelve su contraseña temporal. */
  crearUsuarioInterno: (
    email: string,
    name: string,
    role: 'ADMIN' | 'STAFF' | 'ANALISTA',
    extra?: { recoveryEmail?: string | null; gestionaUsuarios?: boolean },
  ) =>
    request<{ email: string; name: string | null; passwordTemporal: string }>('/users', {
      method: 'POST',
      body: JSON.stringify({ email, name, role, ...extra }),
    }),

  /** Le manda al usuario un enlace para que elija su contraseña. */
  mandarEnlaceDePassword: (id: string) =>
    request<{ destino: string }>(`/users/${id}/enlace-password`, { method: 'POST' }),

  usuariosInternos: () =>
    request<{ users: { id: string; name: string | null; email: string; role: string }[] }>(
      '/users',
    ),

  clients: {
    /** Id y nombre de las empresas, para los filtros. Tambien lo ve un analista. */
    nombres: () => request<{ id: string; name: string }[]>('/clients/nombres'),
    /** Archivar un cliente y sus pedidos. Solo ADMIN. */
    archivar: (id: string, motivo: string) =>
      request<{ archivado: boolean; nombre: string; pedidos: number }>(
        `/clients/${id}/archivar`,
        { method: 'POST', body: JSON.stringify({ motivo }) },
      ),
    desarchivar: (id: string) =>
      request<{ archivado: boolean; nombre: string; pedidos: number }>(
        `/clients/${id}/desarchivar`,
        { method: 'POST' },
      ),
    list: (all = false) => request<Client[]>(`/clients${qs({ all })}`),
    get: (id: string) => request<Client>(`/clients/${id}`),
    create: (body: UpsertClient) =>
      request<Client>('/clients', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpsertClient) =>
      request<Client>(`/clients/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    /**
     * Si el correo ya es de otro usuario, la API responde 409 con un mensaje
     * que dice de quien es; `moverAEsteCliente` confirma que hay que pasarlo.
     */
    crearUsuario: (id: string, email: string, name: string, moverAEsteCliente = false) =>
      request<{ email: string; name: string; passwordTemporal: string }>(
        `/clients/${id}/users`,
        {
          method: 'POST',
          body: JSON.stringify(
            moverAEsteCliente ? { email, name, moverAEsteCliente } : { email, name },
          ),
        },
      ),
    setRequirements: (id: string, body: SetClientRequirements) =>
      request<unknown>(`/clients/${id}/requirements`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    removeContact: (id: string, contactId: string) =>
      request<unknown>(`/clients/${id}/contacts/${contactId}`, {
        method: 'DELETE',
      }),
    addContact: (id: string, body: UpsertClientContact) =>
      request<unknown>(`/clients/${id}/contacts`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  },
};

/** Planilla y ZIP viajan juntos en el mismo multipart. */
async function subirCarga<T>(
  ruta: string,
  planilla: File,
  zip: File | null,
  campos: Record<string, string> = {},
): Promise<T> {
  const fd = new FormData();
  fd.append('planilla', planilla);
  if (zip) fd.append('zip', zip);
  for (const [k, v] of Object.entries(campos)) if (v) fd.append(k, v);

  // Sin Content-Type: el navegador tiene que poner el boundary del multipart.
  const res = await fetch(ruta, { method: 'POST', credentials: 'include', body: fd });
  if (!res.ok) {
    const cuerpo = (await res.json().catch(() => ({}))) as { message?: string };
    throw new ApiError(cuerpo.message ?? `Error ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

/** La ultima consulta de un boton. Las anteriores al 26/10/2026 no traen el detalle. */
export interface UltimaConsulta {
  consultadoEl: string;
  consultadoPor: string | null;
  resultados: ResultadoConsulta[] | null;
  /** «FUENTE: ESTADO», lo unico que guardaban las viejas. */
  resumen: string[];
}
